/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  EventEmitter,
  Output,
} from '@angular/core';
import {MdOption, MdOptgroup} from '@angular/material/core';
import {ActiveDescendantKeyManager} from '@angular/cdk/a11y';
/**
 * Autocomplete IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueAutocompleteIdCounter = 0;
/**
 * Event object that is emitted when an autocomplete option is selected
 */
export class MdAutocompleteSelectedEvent {
/**
 * @param {?} source
 * @param {?} option
 */
constructor(public source: MdAutocomplete,
public option: MdOption) { }
}

function MdAutocompleteSelectedEvent_tsickle_Closure_declarations() {
/** @type {?} */
MdAutocompleteSelectedEvent.prototype.source;
/** @type {?} */
MdAutocompleteSelectedEvent.prototype.option;
}

export class MdAutocomplete implements AfterContentInit {
/**
 * Manages active item in option list based on key events.
 */
_keyManager: ActiveDescendantKeyManager<MdOption>;
/**
 * Whether the autocomplete panel should be visible, depending on option length.
 */
showPanel = false;
/**
 * \@docs-private
 */
template: TemplateRef<any>;
/**
 * Element for the panel containing the autocomplete options.
 */
panel: ElementRef;
/**
 * \@docs-private
 */
options: QueryList<MdOption>;
/**
 * \@docs-private
 */
optionGroups: QueryList<MdOptgroup>;
/**
 * Function that maps an option's control value to its display value in the trigger.
 */
displayWith: ((value: any) => string) | null = null;
/**
 * Event that is emitted whenever an option from the list is selected.
 */
optionSelected: EventEmitter<MdAutocompleteSelectedEvent> =
      new EventEmitter<MdAutocompleteSelectedEvent>();
/**
 * Unique ID to be used by autocomplete trigger's "aria-owns" property.
 */
id: string = `md-autocomplete-${_uniqueAutocompleteIdCounter++}`;
/**
 * @param {?} _changeDetectorRef
 */
constructor(private _changeDetectorRef: ChangeDetectorRef) { }
/**
 * @return {?}
 */
ngAfterContentInit() {
    this._keyManager = new ActiveDescendantKeyManager<MdOption>(this.options).withWrap();
  }
/**
 * Sets the panel scrollTop. This allows us to manually scroll to display options
 * above or below the fold, as they are not actually being focused when active.
 * @param {?} scrollTop
 * @return {?}
 */
_setScrollTop(scrollTop: number): void {
    if (this.panel) {
      this.panel.nativeElement.scrollTop = scrollTop;
    }
  }
/**
 * Returns the panel's scrollTop.
 * @return {?}
 */
_getScrollTop(): number {
    return this.panel ? this.panel.nativeElement.scrollTop : 0;
  }
/**
 * Panel should hide itself when the option list is empty.
 * @return {?}
 */
_setVisibility(): void {
    Promise.resolve().then(() => {
      this.showPanel = !!this.options.length;
      this._changeDetectorRef.markForCheck();
    });
  }
/**
 * Emits the `select` event.
 * @param {?} option
 * @return {?}
 */
_emitSelectEvent(option: MdOption): void {
    const /** @type {?} */ event = new MdAutocompleteSelectedEvent(this, option);
    this.optionSelected.emit(event);
  }
/**
 * Sets a class on the panel based on whether it is visible.
 * @return {?}
 */
_getClassList() {
    return {
      'mat-autocomplete-visible': this.showPanel,
      'mat-autocomplete-hidden': !this.showPanel
    };
  }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-autocomplete, mat-autocomplete',
  templateUrl: 'autocomplete.html',
  styleUrls: ['autocomplete.css'],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'mdAutocomplete, matAutocomplete',
  host: {
    'class': 'mat-autocomplete'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'template': [{ type: ViewChild, args: [TemplateRef, ] },],
'panel': [{ type: ViewChild, args: ['panel', ] },],
'options': [{ type: ContentChildren, args: [MdOption, { descendants: true }, ] },],
'optionGroups': [{ type: ContentChildren, args: [MdOptgroup, ] },],
'displayWith': [{ type: Input },],
'optionSelected': [{ type: Output },],
};
}

function MdAutocomplete_tsickle_Closure_declarations() {
/** @type {?} */
MdAutocomplete.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdAutocomplete.ctorParameters;
/** @type {?} */
MdAutocomplete.propDecorators;
/**
 * Manages active item in option list based on key events.
 * @type {?}
 */
MdAutocomplete.prototype._keyManager;
/**
 * Whether the autocomplete panel should be visible, depending on option length.
 * @type {?}
 */
MdAutocomplete.prototype.showPanel;
/**
 * \@docs-private
 * @type {?}
 */
MdAutocomplete.prototype.template;
/**
 * Element for the panel containing the autocomplete options.
 * @type {?}
 */
MdAutocomplete.prototype.panel;
/**
 * \@docs-private
 * @type {?}
 */
MdAutocomplete.prototype.options;
/**
 * \@docs-private
 * @type {?}
 */
MdAutocomplete.prototype.optionGroups;
/**
 * Function that maps an option's control value to its display value in the trigger.
 * @type {?}
 */
MdAutocomplete.prototype.displayWith;
/**
 * Event that is emitted whenever an option from the list is selected.
 * @type {?}
 */
MdAutocomplete.prototype.optionSelected;
/**
 * Unique ID to be used by autocomplete trigger's "aria-owns" property.
 * @type {?}
 */
MdAutocomplete.prototype.id;
/** @type {?} */
MdAutocomplete.prototype._changeDetectorRef;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
